// Tags we allow to pass through from Airtable content. Anything not in this
// list is HTML-escaped so it renders as literal text.
const ALLOWED_TAGS: Record<string, readonly string[]> = {
  p: [],
  br: [],
  hr: [],
  strong: [],
  b: [],
  em: [],
  i: [],
  u: [],
  s: [],
  ul: [],
  ol: [],
  li: [],
  a: ['href', 'target', 'rel', 'title'],
  h1: [],
  h2: [],
  h3: [],
  h4: [],
  h5: [],
  h6: [],
  blockquote: [],
  code: [],
  pre: [],
  span: [],
  div: [],
};

const VOID_TAGS = new Set(['br', 'hr']);

// Tags whose contents are raw text, not markup. We drop the element and its body
// outright rather than letting the tokenizer escape the source into visible text.
const RAW_TEXT_TAGS = ['script', 'style'];

// Remove <script>/<style> elements (with their bodies) and HTML comments before
// tokenizing. This is a plain character scan rather than a regex tag-filter: a
// regex cannot reliably match every closing form (`</script foo>`, an unclosed
// tag, a body containing `>`), which is the root of the CodeQL "incomplete
// multi-character sanitization" finding. The scan repeats until stable because
// dropping one element can splice the surrounding text into a fresh one
// (e.g. `<scr<script></script>ipt>`).
const stripRawElements = (input: string): string => {
  let current = input;
  let previous: string;

  do {
    previous = current;
    const lower = current.toLowerCase();
    let result = '';
    let i = 0;

    while (i < current.length) {
      const lt = current.indexOf('<', i);
      if (lt === -1) {
        result += current.slice(i);
        break;
      }
      result += current.slice(i, lt);

      if (lower.startsWith('<!--', lt)) {
        const end = current.indexOf('-->', lt + 4);
        if (end === -1) {
          // Unterminated comment: keep the literal `<` (the tokenizer escapes
          // it) instead of dropping all trailing content.
          result += '<';
          i = lt + 1;
        } else {
          i = end + 3;
        }
        continue;
      }

      const rawTag = RAW_TEXT_TAGS.find((tag) => {
        if (!lower.startsWith(`<${tag}`, lt)) return false;
        // Reject look-alikes such as `<scripting>`: the tag name must end here.
        const next = current[lt + 1 + tag.length];
        return next === undefined || ' \t\n\r\f/>'.indexOf(next) !== -1;
      });

      if (rawTag) {
        const close = lower.indexOf(`</${rawTag}`, lt);
        if (close === -1) {
          // Unterminated raw element: keep the literal `<` (the tokenizer
          // escapes it) instead of dropping all trailing content.
          result += '<';
          i = lt + 1;
          continue;
        }
        const end = current.indexOf('>', close);
        i = end === -1 ? current.length : end + 1;
        continue;
      }

      // Not a comment or raw-text element: keep the `<` and resume scanning.
      result += '<';
      i = lt + 1;
    }

    current = result;
  } while (current !== previous);

  return current;
};

const escapeText = (input: string) =>
  input
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');

const escapeAttrValue = (value: string) =>
  value.replaceAll('&', '&amp;').replaceAll('"', '&quot;');

const sanitizeAttributes = (tag: string, raw: string) => {
  const allowed = ALLOWED_TAGS[tag] || [];
  if (!allowed.length || !raw.trim()) return '';

  const attrRe = /([a-zA-Z_:][a-zA-Z0-9_.:-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/g;
  const out: string[] = [];
  let m: RegExpExecArray | null;

  while ((m = attrRe.exec(raw)) !== null) {
    const name = m[1].toLowerCase();
    if (!allowed.includes(name)) continue;
    if (name.startsWith('on')) continue;

    const value = (m[2] ?? m[3] ?? m[4] ?? '').trim();

    if (name === 'href') {
      const lowered = value.toLowerCase();
      if (
        lowered.startsWith('javascript:') ||
        lowered.startsWith('data:') ||
        lowered.startsWith('vbscript:')
      ) {
        continue;
      }
    }

    out.push(`${name}="${escapeAttrValue(value)}"`);
  }

  return out.length ? ` ${out.join(' ')}` : '';
};

// Walk the input, keep whitelisted tags, escape everything else. Strips
// <script>, <style>, and HTML comments entirely (along with their contents for
// the first two).
const sanitizeHtml = (input: string): string => {
  const stripped = stripRawElements(input);

  const tokenRe = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)([^>]*?)(\/?)>/g;
  let result = '';
  let lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = tokenRe.exec(stripped)) !== null) {
    result += escapeText(stripped.slice(lastIndex, m.index));

    const isClose = m[1] === '/';
    const tagName = m[2].toLowerCase();
    const attrs = m[3] || '';
    const selfClosing = m[4] === '/' || VOID_TAGS.has(tagName);

    lastIndex = m.index + m[0].length;

    if (!(tagName in ALLOWED_TAGS)) {
      result += escapeText(m[0]);
      continue;
    }

    if (isClose) {
      result += `</${tagName}>`;
    } else if (selfClosing) {
      result += `<${tagName}${sanitizeAttributes(tagName, attrs)}/>`;
    } else {
      result += `<${tagName}${sanitizeAttributes(tagName, attrs)}>`;
    }
  }

  result += escapeText(stripped.slice(lastIndex));
  return result;
};

const applyWrappedMarker = (
  input: string,
  marker: string,
  openTag: string,
  closeTag: string
) => {
  let result = input;
  let start = result.indexOf(marker);

  while (start !== -1) {
    const end = result.indexOf(marker, start + marker.length);
    if (end === -1) break;

    const inner = result.slice(start + marker.length, end);
    result =
      result.slice(0, start) +
      openTag +
      inner +
      closeTag +
      result.slice(end + marker.length);

    start = result.indexOf(
      marker,
      start + openTag.length + inner.length + closeTag.length
    );
  }

  return result;
};

const isOrderedListLine = (line: string) => {
  if (!line.length) return false;

  let index = 0;
  while (index < line.length && line[index] >= '0' && line[index] <= '9') {
    index += 1;
  }

  return index > 0 && line[index] === '.' && line[index + 1] === ' ';
};

const BLOCK_TAG_RE = /<\/?(p|br|ul|ol|li|h[1-6]|blockquote|pre|div|hr)\b/i;

export const markdownToInlineHtml = (value?: string | null) => {
  if (!value) return '';

  const sanitized = sanitizeHtml(value);
  const hasBlockHtml = BLOCK_TAG_RE.test(sanitized);

  const normalized = sanitized
    .replaceAll('\r\n', '\n')
    .replaceAll('\r', '\n');

  // If the content already carries block-level HTML from Airtable, trust its
  // structure and skip the forced line-break conversion. Otherwise fall back
  // to the plain-text behavior: lines → <br/>, `- item` → `• item`.
  const lineFormatted = hasBlockHtml
    ? normalized
    : normalized
        .split('\n')
        .map((line) => {
          const trimmed = line.trim();

          if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            return `• ${trimmed.slice(2)}`;
          }

          if (isOrderedListLine(trimmed)) {
            return trimmed;
          }

          return line;
        })
        .join('<br/>');

  const withBold = applyWrappedMarker(
    applyWrappedMarker(lineFormatted, '**', '<strong>', '</strong>'),
    '__',
    '<strong>',
    '</strong>'
  );

  return applyWrappedMarker(
    applyWrappedMarker(withBold, '*', '<em>', '</em>'),
    '_',
    '<em>',
    '</em>'
  );
};
